// ActivePatientsWidget.js
// Floating widget mounted app-wide (App.js) for rad techs, lab techs, office
// assistants, and admins — the RIS counterpart to the EMR's
// ReadyPatientsWidget/WaitingPatientsWidget. It answers the two questions a tech
// (or OA/admin staffing the front desk) otherwise has to leave their current page
// to check:
//
//   New Orders     — orders that have landed on the Order Worklist since the tech
//                    last acknowledged them, so a fresh order announces itself
//                    instead of waiting to be noticed on the next visit to the page.
//   Active Patients — patients physically in the building: checked in, past
//                    Registration, not yet Complete. Each one counts up from their
//                    check-in time and colours by how long they've been waiting, so
//                    nobody is left sitting in a hallway. Both lab and imaging
//                    orders are tracked, since a tech covering either area is the
//                    person who'd notice.
//
// Both lists come from a single /orders/activityFeed poll. Acknowledged orders are
// remembered in localStorage per user, so the badge means "new to you" rather than
// "pending", and reloading the page doesn't re-alert on everything.
import logger from '../../utils/logger';
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalStateContext } from '../../utils/GlobalStateContext';
import { getUserId, convertUTCToLocal, formatTo12Hour } from '../../utils/utils';
import { Bell, X, Scan, FlaskConical, Clock, MapPin } from 'lucide-react';
import '../../style/ActivePatientsWidget.css';

const REFRESH_INTERVAL_MS = 30000;

// These are the only roles this widget is for; everyone else gets nothing rendered.
const ELIGIBLE_ROLES = ['rad_tech', 'lab_tech', 'office_assistant', 'admin'];

// A patient in the building longer than this hasn't necessarily been forgotten,
// but they're worth a second look — and past the red mark, worth chasing.
const WAIT_TIER_YELLOW_SECONDS = 15 * 60;
const WAIT_TIER_RED_SECONDS = 30 * 60;

const waitTier = (totalSeconds) => {
  if (totalSeconds >= WAIT_TIER_RED_SECONDS) return 'red';
  if (totalSeconds >= WAIT_TIER_YELLOW_SECONDS) return 'yellow';
  return 'green';
};

const formatWait = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

// Stable identity for an order across polls — ids are only unique within their
// own table, so the type has to be part of the key.
const orderKey = (order) => `${order.order_type}:${order.order_id}`;

const ackStorageKey = (user) => `ris.acknowledgedOrders.${user || 'unknown'}`;

const readAcknowledged = (user) => {
  try {
    const raw = localStorage.getItem(ackStorageKey(user));
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch (error) {
    logger.error('Error reading acknowledged orders', error);
    return new Set();
  }
};

const writeAcknowledged = (user, keys) => {
  try {
    localStorage.setItem(ackStorageKey(user), JSON.stringify([...keys]));
  } catch (error) {
    logger.error('Error saving acknowledged orders', error);
  }
};

export default function ActivePatientsWidget() {
  const { currentUser, setCurrentPatient } = useContext(GlobalStateContext);
  const BASE_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const user = getUserId(currentUser);

  const [open, setOpen] = useState(false);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [activePatients, setActivePatients] = useState([]);
  const [acknowledged, setAcknowledged] = useState(() => readAcknowledged(getUserId(currentUser)));
  const [nowMs, setNowMs] = useState(Date.now());

  const eligible = ELIGIBLE_ROLES.includes(currentUser?.type);

  const loadFeed = useCallback(async () => {
    if (!eligible || !user) return;
    try {
      const response = await fetch(
        `${BASE_URL}/orders/activityFeed?user=${user}&type=${currentUser.type}`
      );
      if (!response.ok) return;
      const data = await response.json();
      const orders = data.pendingOrders || [];
      setPendingOrders(orders);
      setActivePatients(data.activePatients || []);

      // Drop acknowledgements for orders that have left the worklist, so the
      // stored list tracks the worklist instead of growing without bound.
      const liveKeys = new Set(orders.map(orderKey));
      setAcknowledged((prev) => {
        const pruned = new Set([...prev].filter((k) => liveKeys.has(k)));
        if (pruned.size !== prev.size) writeAcknowledged(user, pruned);
        return pruned.size !== prev.size ? pruned : prev;
      });
    } catch (error) {
      logger.error('Error fetching active patient feed', error);
    }
  }, [BASE_URL, eligible, user, currentUser?.type]);

  useEffect(() => {
    loadFeed();
    const pollId = setInterval(loadFeed, REFRESH_INTERVAL_MS);
    return () => clearInterval(pollId);
  }, [loadFeed]);

  // Local 1s ticker advances the wait clocks between polls, so the timers read
  // smoothly without hammering the API just to move a clock forward.
  useEffect(() => {
    if (!eligible) return;
    const tickId = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(tickId);
  }, [eligible]);

  const acknowledge = (keys) => {
    setAcknowledged((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      writeAcknowledged(user, next);
      return next;
    });
  };

  const goToOrder = (order) => {
    acknowledge([orderKey(order)]);
    setOpen(false);
    // OrderWorklist auto-opens the scheduling modal for the order named in
    // location.state, so this lands the tech directly on the new order.
    navigate('/orderWorklist', {
      state: { orderId: order.order_id, orderType: order.order_type },
    });
  };

  const goToPatient = (patient) => {
    setOpen(false);
    setCurrentPatient({
      ris_patients_id: patient.ris_patients_id,
      new_patients_id: patient.new_patients_id,
      first_name: patient.first_name,
      last_name: patient.last_name,
      appointment_start: patient.appointment_start,
      visit_type: patient.visit_type,
      location: patient.location,
      check_in_time: patient.check_in_time,
    });
    navigate('/patientAbout');
  };

  if (!eligible) return null;

  const newOrders = pendingOrders.filter((o) => !acknowledged.has(orderKey(o)));

  const patientsWithWait = activePatients
    .map((patient) => {
      const checkedIn = convertUTCToLocal(patient.check_in_time);
      return {
        ...patient,
        elapsedSeconds: checkedIn ? (nowMs - checkedIn.getTime()) / 1000 : null,
      };
    })
    // Longest in the building first — that's who's most likely been forgotten.
    .sort((a, b) => (b.elapsedSeconds ?? -1) - (a.elapsedSeconds ?? -1));

  const attentionCount = newOrders.length + patientsWithWait.length;
  if (attentionCount === 0) return null;

  const longestWait = patientsWithWait[0]?.elapsedSeconds ?? 0;
  const urgencyTier = waitTier(longestWait);

  return (
    <div className={`apw-root apw-root--${urgencyTier}`}>
      {open && (
        <div className="apw-panel">
          <div className="apw-panel-header">
            <span>Activity Widget</span>
            <button className="apw-close-btn" onClick={() => setOpen(false)} aria-label="Close">
              <X size={15} />
            </button>
          </div>

          <div className="apw-panel-body">
            {/* ── Patients currently in the building ── */}
            <div className="apw-section">
              <div className="apw-section-header">
                <span>Active Patients ({patientsWithWait.length})</span>
              </div>

              {patientsWithWait.length === 0 ? (
                <p className="apw-empty">No patients currently being seen.</p>
              ) : (
                patientsWithWait.map((patient) => {
                  const tier = patient.elapsedSeconds === null
                    ? 'green'
                    : waitTier(patient.elapsedSeconds);
                  return (
                    <div
                      key={patient.schedule_id}
                      className={`apw-card apw-card--patient apw-card--${tier}`}
                    >
                      <div className="apw-card-info">
                        <span className="apw-card-name">
                          {patient.first_name} {patient.last_name}
                        </span>
                        <span className="apw-card-meta">
                          <MapPin size={12} /> {patient.location}
                          {patient.visit_type ? ` · ${patient.visit_type}` : ''}
                        </span>
                        <span className={`apw-card-wait apw-card-wait--${tier}`}>
                          <Clock size={12} />
                          {patient.elapsedSeconds === null
                            ? 'Not checked in'
                            : `In facility ${formatWait(patient.elapsedSeconds)}`}
                          {patient.check_in_time && ` (since ${formatTo12Hour(patient.check_in_time)})`}
                        </span>

                        {patient.orders?.length > 0 && (
                          <div className="apw-order-pills">
                            {patient.orders.map((order) => (
                              <span
                                key={orderKey(order)}
                                className={`apw-order-pill apw-order-pill--${order.order_type.toLowerCase()}`}
                                title={`${order.order_type} · ${order.status}`}
                              >
                                {order.order_type === 'Lab'
                                  ? <FlaskConical size={11} />
                                  : <Scan size={11} />}
                                {order.exam_ordered}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="apw-card-actions">
                        <button className="apw-primary-btn" onClick={() => goToPatient(patient)}>
                          View
                        </button>
                        <button
                          className="apw-secondary-btn"
                          onClick={() => { setOpen(false); navigate('/checkIn'); }}
                        >
                          Check-In
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* ── New orders on the worklist ── */}
            <div className="apw-section">
              <div className="apw-section-header">
                <span>New Orders ({newOrders.length})</span>
                {newOrders.length > 0 && (
                  <button
                    className="apw-ack-all-btn"
                    onClick={() => acknowledge(pendingOrders.map(orderKey))}
                  >
                    Mark all seen
                  </button>
                )}
              </div>

              {newOrders.length === 0 ? (
                <p className="apw-empty">No new orders.</p>
              ) : (
                newOrders.map((order) => {
                  const OrderIcon = order.order_type === 'Lab' ? FlaskConical : Scan;
                  return (
                    <div key={orderKey(order)} className="apw-card apw-card--order">
                      <div className="apw-card-icon">
                        <OrderIcon size={16} />
                      </div>
                      <div className="apw-card-info">
                        <span className="apw-card-name">
                          {order.first_name} {order.last_name}
                        </span>
                        <span className="apw-card-meta">
                          {order.exam_ordered}
                          {order.type ? ` · ${order.type}` : ''}
                        </span>
                        <span className="apw-card-meta">
                          Order #{order.order_id}
                          {order.priority ? ` · ${order.priority}` : ''}
                        </span>
                      </div>
                      <div className="apw-card-actions">
                        <button className="apw-primary-btn" onClick={() => goToOrder(order)}>
                          Schedule
                        </button>
                        <button
                          className="apw-secondary-btn"
                          onClick={() => acknowledge([orderKey(order)])}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      <button
        className={`apw-toggle-btn apw-toggle-btn--${urgencyTier}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Worklist activity"
      >
        <Bell size={15} />
        <span>Activity</span>
        <span className="apw-toggle-count">{attentionCount}</span>
      </button>
    </div>
  );
}
